import { Assets, Rectangle, Sprite, Texture } from "pixi.js";
import { SHADOW_SHAPES } from "@talent/tasks";
import { ATLAS_CELL, ATLAS_COLS, COLOUR_ATLAS, SHADOW_ATLAS } from "./animal-atlas.js";
import type { CardPainter } from "./choice-task.js";
import { COLOR } from "./paint.js";

/**
 * The shadow game's cards: the silhouette above, the animal below.
 *
 * **Both come out of two atlases built from the source artwork**, not from paths drawn here. The
 * first version of this file drew twelve primitives — an apple, a leaf, a sun — and it was the
 * wrong call twice over: hand-drawn primitives are not what a child recognises, and the outlines
 * of an apple and a pear are similar because I asserted they were rather than because anything in
 * the world made them so. Real animals are what the printed version of this activity has always
 * used, and a horse next to a donkey is a comparison nobody has to be talked into.
 *
 * `apps/web/scripts/make-animal-atlas.py` builds the two sheets and explains how the background
 * comes off. The important part for this file is what it guarantees: a cell holds one animal,
 * centred, on transparency — so the silhouette is not a separate drawing that could disagree with
 * the animal, it is the same cut-out with its colours removed.
 */

let frames: { colour: Texture[]; shadow: Texture[] } | null = null;

/**
 * Loads the atlases and slices them into per-animal textures. Idempotent.
 *
 * **Awaited before the game opens, never inside a trial**, for the same reason the letters game
 * waits for its webfont: a card painted before the texture has arrived is painted empty, and it
 * stays empty until something else forces a repaint. In a game about looking at one picture, that
 * is not a slow load — it is the wrong stimulus.
 */
export async function ensureAnimalAtlas(): Promise<void> {
  if (frames) return;
  const [colourSheet, shadowSheet] = await Promise.all([
    Assets.load<Texture>(COLOUR_ATLAS),
    Assets.load<Texture>(SHADOW_ATLAS),
  ]);

  const slice = (sheet: Texture): Texture[] =>
    Array.from({ length: SHADOW_SHAPES }, (_, i) => {
      const x = (i % ATLAS_COLS) * ATLAS_CELL;
      const y = Math.floor(i / ATLAS_COLS) * ATLAS_CELL;
      return new Texture({
        source: sheet.source,
        frame: new Rectangle(x, y, ATLAS_CELL, ATLAS_CELL),
      });
    });

  frames = { colour: slice(colourSheet), shadow: slice(shadowSheet) };
}

/**
 * The card painter handed to the shared choice renderer.
 *
 * `state === "target"` is the silhouette. **It is drawn pale rather than black**, and that is the
 * stage rather than a whim: `--color-stage` is a deep purple, so a dark outline on it is a shape a
 * child cannot see. Cream inverts the value and keeps the only thing that matters — one flat
 * region, no interior detail, outline alone. It is also the convention the printed worksheets this
 * game comes from already use.
 *
 * Everything else is the animal in its own colours. The right/wrong colours reach the card through
 * the panel behind it rather than by repainting the animal, because a fox that turned green when
 * the child was right would stop being the fox they picked.
 */
export const paintShadowCard: CardPainter = (g, content, size, state) => {
  const id = content[0];
  if (id === undefined || !frames) return;

  const texture =
    state === "target" ? frames.shadow[id] : frames.colour[id];
  if (!texture) return;

  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5);
  // The atlas cell already carries its own margin, so the card's padding is small. The target is
  // given a touch more room than an option because it is the thing being read rather than chosen.
  const fit = size * (state === "target" ? 0.94 : 0.86);
  sprite.width = fit;
  sprite.height = fit;
  if (state === "target") sprite.tint = COLOR.cream;
  g.addChild(sprite);
};
